import React, { useState, useEffect, useRef } from 'react';
import programmeService from '../../services/programmeService';
import InstitutionFilter from '../blocks/InstitutionFilter';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../blocks/LoadingSpinner';
import { FaPlusCircle } from 'react-icons/fa';
import {
    extractInstitutionAliasQuery,
    getInstitutionShortLabel,
} from '../../utils/institutionAssets';
import {
  getProgrammePrimaryTitle,
  getProgrammeSecondaryTitle
} from '../../utils/programmeDisplay';
import { JUPAS_VERY_SAFE_ACTION_CLASS } from '../../utils/jupasClassificationDisplay';
import {
  JUPAS_PROGRAMME_BAR_ACTION_BUTTON_CLASS,
  JUPAS_PROGRAMME_BAR_ACTION_INNER_CLASS,
  JUPAS_PROGRAMME_BAR_IDENTITY_CLASS,
  JUPAS_PROGRAMME_BAR_MAIN_CLASS,
  JUPAS_PROGRAMME_BAR_ROW_CLASS,
  JUPAS_PROGRAMME_BAR_TITLE_CLASS,
  JUPAS_PROGRAMME_BAR_TRAILING_CLASS,
  JUPAS_PROGRAMME_CODE_LABEL_CLASS,
} from '../../utils/jupasLayoutDisplay';

const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const ProgrammeSearch = ({
    selectedCollection,
    onProgrammeSelect,
    selectedProgrammesCount = 0,
    selectedProgrammeCodes = [],
    onInstitutionsChange,
    selectedInstitutions = [],
    selectedRequirementFilters = [],
    onRequirementFiltersChange,
    selectedClassificationFilters = [],
    onClassificationFiltersChange,
    filterProgramme,
    includeFilters = true,
    includeSearch = true,
    inlineResults = false,
    onSearchStateChange,
}) => {
    const { t, i18n } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [localSelectedInstitutions, setLocalSelectedInstitutions] = useState(selectedInstitutions);
    const [error, setError] = useState(null);
    const searchRef = useRef(null);
    const inputRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const rawSearchResultsRef = useRef([]);
    const filterProgrammeRef = useRef(filterProgramme);
    filterProgrammeRef.current = filterProgramme;

    useEffect(() => {
        if (!includeSearch || !inlineResults || !onSearchStateChange) return;
        onSearchStateChange({
            query: searchQuery,
            results: searchResults,
            loading: isLoading,
            error,
        });
    }, [searchQuery, searchResults, isLoading, error, includeSearch, inlineResults, onSearchStateChange]);

    // Handle click outside to close dropdown (dropdown mode only)
    useEffect(() => {
        if (inlineResults) return undefined;
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [inlineResults]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Sync local state with parent's selectedInstitutions prop
    useEffect(() => {
        setLocalSelectedInstitutions(selectedInstitutions);
    }, [selectedInstitutions]);

    const performSearch = async (query, institutions = localSelectedInstitutions) => {
        const parsedQuery = extractInstitutionAliasQuery(query);
        const effectiveInstitutions = parsedQuery.institution
            ? [parsedQuery.institution]
            : institutions;
        const effectiveQuery = parsedQuery.query;

        if (parsedQuery.institution) {
            const institutionSelectionChanged = (
                localSelectedInstitutions.length !== 1
                || localSelectedInstitutions[0] !== parsedQuery.institution
            );
            if (institutionSelectionChanged) {
                setLocalSelectedInstitutions(effectiveInstitutions);
                onInstitutionsChange?.(effectiveInstitutions);
            }
        }

        if (!effectiveQuery.trim() && !parsedQuery.institution) {
            rawSearchResultsRef.current = [];
            setSearchResults([]);
            if (!inlineResults) {
                setShowDropdown(false);
            }
            setError(null);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            const result = await programmeService.searchProgrammes(
                effectiveQuery,
                effectiveInstitutions.length > 0 ? effectiveInstitutions : null,
                selectedCollection
            );

            if (result.success) {
                rawSearchResultsRef.current = result.data;
                const currentFilter = filterProgrammeRef.current;
                setSearchResults(currentFilter ? result.data.filter(currentFilter) : result.data);
                if (!inlineResults) {
                    setShowDropdown(true);
                }
            } else {
                setError(t('common.searchFailed'));
                rawSearchResultsRef.current = [];
                setSearchResults([]);
            }
        } catch (err) {
            console.error('Error searching programmes:', err);
            setError(t('common.searchError'));
            rawSearchResultsRef.current = [];
            setSearchResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const currentFilter = filterProgrammeRef.current;
        setSearchResults(currentFilter
            ? rawSearchResultsRef.current.filter(currentFilter)
            : rawSearchResultsRef.current);
    }, [selectedRequirementFilters, selectedClassificationFilters, filterProgramme]);

    const handleSearchInputChange = (query) => {
        setSearchQuery(query);
        if (!query.trim()) {
            rawSearchResultsRef.current = [];
            setSearchResults([]);
        }

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Debounce the search - wait 300ms after user stops typing
        searchTimeoutRef.current = setTimeout(() => {
            performSearch(query);
        }, 300);
    };

    const handleInstitutionsChange = (institutions) => {
        setLocalSelectedInstitutions(institutions);
        // Call parent callback if provided
        if (onInstitutionsChange) {
            onInstitutionsChange(institutions);
        }
        // Re-run search if there's an active query with debouncing
        if (searchQuery.trim()) {
            // Clear previous timeout
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }

            // Debounce the search
            searchTimeoutRef.current = setTimeout(() => {
                performSearch(searchQuery, institutions);
            }, 300);
        }
    };

    const handleProgrammeClick = (programme) => {
        onProgrammeSelect(programme);
        setSearchQuery('');
        rawSearchResultsRef.current = [];
        setSearchResults([]);
        setShowDropdown(false);
        // Keep focus on input after selection
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const highlightMatch = (text, query) => {
        if (!text || !query) return text;

        const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) =>
            index % 2 === 1 ? (
                <span key={index} className="bg-yellow-200 font-semibold dark:bg-yellow-700/60">{part}</span>
            ) : (
                part
            )
        );
    };

    return (
        <div className={includeFilters && includeSearch ? 'space-y-3' : ''}>
            {includeFilters ? (
                <InstitutionFilter
                    selectedCollection={selectedCollection}
                    onInstitutionChange={handleInstitutionsChange}
                    selectedInstitutions={localSelectedInstitutions}
                    selectedRequirementFilters={selectedRequirementFilters}
                    onRequirementFiltersChange={onRequirementFiltersChange}
                    selectedClassificationFilters={selectedClassificationFilters}
                    onClassificationFiltersChange={onClassificationFiltersChange}
                />
            ) : null}

            {includeSearch ? (
            <div className="relative" ref={searchRef}>
                <div className="relative">
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleSearchInputChange(e.target.value)}
                        placeholder={t('jupa.searchByProgrammeCode')}
                        className="w-full rounded-lg border border-edge-strong px-4 py-2 pr-12 focus:border-primary focus:outline-none focus:ring-1 focus:ring-inset focus:ring-primary/25"
                    />
                    {isLoading ? (
                        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                            <LoadingSpinner size="small" />
                        </div>
                    ) : null}
                </div>

                {!inlineResults && showDropdown && searchResults.length > 0 && (
                    <div
                        className="absolute z-50 mt-1 max-h-96 w-full overflow-y-auto overflow-x-hidden rounded-lg border border-edge-strong bg-surface shadow-lg"
                        onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
                    >
                        {searchResults.map((programme) => {
                            const isSelected = selectedProgrammeCodes.includes(programme.code);
                            return (
                            <div
                                key={programme.code}
                                className={`${JUPAS_PROGRAMME_BAR_ROW_CLASS} border-b border-edge last:border-b-0 hover:bg-surface-muted`}
                            >
                                <div className={JUPAS_PROGRAMME_BAR_MAIN_CLASS}>
                                    <div className={JUPAS_PROGRAMME_BAR_IDENTITY_CLASS}>
                                        <span className={JUPAS_PROGRAMME_CODE_LABEL_CLASS}>
                                            <span className="truncate">{highlightMatch(programme.code, searchQuery)}</span>
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleProgrammeClick(programme)}
                                            className={`${JUPAS_PROGRAMME_BAR_TITLE_CLASS} focus:outline-none`}
                                        >
                                            <p className="truncate text-sm font-medium text-ink">
                                                {highlightMatch(getProgrammePrimaryTitle(programme, i18n.language), searchQuery)}
                                            </p>
                                            <p className="mt-0.5 truncate text-xs text-ink-subtle">
                                                {getInstitutionShortLabel(programme.institution, i18n.language)}
                                                {programme.short_name ? ` · ${programme.short_name}` : ''}
                                            </p>
                                        </button>
                                    </div>
                                </div>
                                <div className={JUPAS_PROGRAMME_BAR_TRAILING_CLASS}>
                                    <button
                                        type="button"
                                        disabled={isSelected}
                                        onClick={() => handleProgrammeClick(programme)}
                                        className={`${JUPAS_PROGRAMME_BAR_ACTION_BUTTON_CLASS} ${
                                            isSelected ? 'text-ink-muted' : JUPAS_VERY_SAFE_ACTION_CLASS
                                        }`}
                                        aria-label={isSelected ? t('jupa.selected') : t('jupa.add')}
                                        title={isSelected ? t('jupa.selected') : t('jupa.add')}
                                    >
                                        <span className={`${JUPAS_PROGRAMME_BAR_ACTION_INNER_CLASS} ${
                                            isSelected ? '' : 'group-hover:bg-green-50 group-active:bg-green-100'
                                        }`}>
                                            <FaPlusCircle className="h-4 w-4" aria-hidden />
                                        </span>
                                    </button>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}

                {!inlineResults && showDropdown && searchQuery && searchResults.length === 0 && !isLoading && !error && (
                    <div
                        className="absolute z-50 mt-1 w-full rounded-lg border border-edge-strong bg-surface p-4 shadow-lg"
                        onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
                    >
                        <p className="text-center text-ink-subtle">{t('jupa.noProgrammesFoundMatching') + ' "' + searchQuery + '"'}</p>
                    </div>
                )}

                {!inlineResults && error && (
                    <div
                        className="absolute z-50 mt-1 w-full rounded-lg border border-red-300 bg-surface p-4 shadow-lg"
                        onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
                    >
                        <p className="text-center text-red-600">{error}</p>
                    </div>
                )}
            </div>
            ) : null}
        </div>
    );
};

export default ProgrammeSearch;
